'use client';

import { useEffect, useState, useRef, useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Rating } from '@mantine/core';
import Autoplay from 'embla-carousel-autoplay';

import { Title } from '@/styles/global.elements';
import {
  Container,
  Testimonials,
  Testimonial,
  InnerTestimonial,
  ImageWrap,
  Comment,
  StudentName,
  EducationLink,
  Bottom,
  Subject,
} from './testimonials-widget.elements';

import { useDeviceSize } from '@/hooks/use-device-size.hook';
import { breakpoints } from '@/styles/theme';

const TestimonialsWidget = ({ testimonials, title }) => {
  const [width] = useDeviceSize();
  const [mounted, setMounted] = useState(false);
  const emblaApi = useRef(null);
  const isMounted = useRef(true);

  const shouldUseAutoplay = testimonials.length > 1;

  // Create autoplay instance once with useRef - stable reference (only if needed)
  const autoplay = useRef(
    shouldUseAutoplay ? Autoplay({ delay: 5000, stopOnInteraction: false }) : null
  );

  useEffect(() => {
    isMounted.current = true;
    setMounted(true);

    return () => {
      isMounted.current = false;
      // Stop autoplay before unmount to prevent stale callbacks
      try {
        autoplay.current?.stop?.();
      } catch {
        // Ignore errors during cleanup
      }
    };
  }, []);

  // Handle Embla API when carousel mounts
  const handleEmblaApi = useCallback((api) => {
    if (!isMounted.current) return;
    emblaApi.current = api;
  }, []);

  const getSlidesToScroll = useCallback(() => {
    if (width > breakpoints.abs.desktop) {
      return 3;
    } else if (width > breakpoints.abs.laptop) {
      return 2;
    } else {
      return 1;
    }
  }, [width]);

  const handleMouseEnter = useCallback(() => {
    if (isMounted.current) {
      autoplay.current?.stop?.();
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (isMounted.current) {
      autoplay.current?.play?.();
    }
  }, []);

  if (!mounted || !width || testimonials.length === 0) return null;

  return (
    <Container>
      <Title $medium>{title}</Title>
      <Testimonials
        withIndicators={false}
        withControls={testimonials.length > 1}
        plugins={shouldUseAutoplay && autoplay.current ? [autoplay.current] : []}
        getEmblaApi={handleEmblaApi}
        onMouseEnter={shouldUseAutoplay ? handleMouseEnter : undefined}
        onMouseLeave={shouldUseAutoplay ? handleMouseLeave : undefined}
        emblaOptions={{ loop: shouldUseAutoplay, slidesToScroll: getSlidesToScroll() }}
      >
        {testimonials.map(
          ({ id, comment, grade, education: { title, slug }, studentName, registration }) => (
            <Testimonial key={id}>
              <InnerTestimonial>
                {registration?.student?.profileImage ? (
                  <ImageWrap>
                    <Image
                      src={registration.student.profileImage.sizes.midSquare.url}
                      alt={studentName}
                      width="96"
                      height="96"
                      style={{ objectFit: 'cover' }}
                    />
                  </ImageWrap>
                ) : null}
                <Comment>{comment}</Comment>
                <Bottom>
                  <Subject>
                    <StudentName>{studentName}</StudentName>
                    <Rating id={`testimonial-rating-${id}`} value={grade} fractions={1} readOnly />
                  </Subject>
                  <Link href={`/${slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <EducationLink>{title}</EducationLink>
                  </Link>
                </Bottom>
              </InnerTestimonial>
            </Testimonial>
          )
        )}
      </Testimonials>
    </Container>
  );
};

export { TestimonialsWidget };
